import React, { useEffect, useState } from 'react';
import AppLayout from '../../../components/layout/AppLayout';
import { useTranslation } from 'react-i18next';
import { useWorkflowApi } from '../../../api/workflow/workflow';
import { TWorkflow } from '../../../models/workflow/workflow';
import { Button, Input, message, Space, Spin, Table } from 'antd';
import {
  SaveOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import ConfirmationDialog from '../../../components/layout/ConfirmationDialog';

const WorkflowList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getWorkflows, createWorkflow, updateWorkflow, deleteWorkflow } = useWorkflowApi();

  const [workflows, setWorkflows] = useState<TWorkflow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [addingId, setAddingId] = useState<number>(0);
  const [loadingRow, setLoadingRow] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(false);

  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editingWorkflow, setEditingWorkflow] = useState<TWorkflow | null>(null);

  // 1) Charger la liste, en garantissant toujours un tableau
  useEffect(() => {
    setLoading(true);
    getWorkflows()
      .then(resp => {
        const list = Array.isArray(resp.data) ? resp.data : [];
        setWorkflows(list);
      })
      .catch(err => message.error(err.response?.data ?? err.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  const edit = (wf: TWorkflow) => {
    setEditingKey(wf.id);
    setEditingWorkflow({ ...wf });
  };

  // 2) Ajouter un workflow vide, protège contre un état non itérable
  const handleAdd = () => {
    const idRow = addingId - 1;
    const newItem: TWorkflow = {
      id: idRow,
      name: '',
      code: '',
    };
    setAddingId(idRow);
    setEditingKey(idRow);
    setEditingWorkflow(newItem);

    setWorkflows(prev =>
      Array.isArray(prev)
        ? [newItem, ...prev]
        : [newItem]
    );
  };

  const save = (id: number) => {
    if (!editingWorkflow) return;
    setLoadingRow(id);
    const action =
      editingWorkflow.id > 0
        ? updateWorkflow(editingWorkflow.id, editingWorkflow)
        : createWorkflow(editingWorkflow);

    action
      .then(resp => {
        const saved: TWorkflow = resp.data;
        setWorkflows(prev =>
          prev.map(w => (w.id === id ? saved : w))
        );
        message.success(t('message.savedSuccessfully'));
      })
      .catch(err => message.error(err.response?.data ?? err.message))
      .finally(() => {
        setEditingKey(null);
        setEditingWorkflow(null);
        setLoadingRow(null);
      });
  };

  const cancel = () => {
    setEditingKey(null);
    setEditingWorkflow(null);
  };

  const showDetails = (wf: TWorkflow) => {
    navigate('/workflow-steps', { state: { workflow: wf } });
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setIsDeleteDialogVisible(true);
  };

  const handleDelete = () => {
    if (deleteId == null) return;
    setDeleteLoading(true);
    deleteWorkflow(deleteId)
      .then(() => {
        setWorkflows(prev => prev.filter(w => w.id !== deleteId));
        message.success(t('message.deletedSuccessfully'));
      })
      .catch(err => message.error(err.response?.data ?? err.message))
      .finally(() => {
        setDeleteLoading(false);
        setIsDeleteDialogVisible(false);
        setDeleteId(null);
      });
  };

  const columns: ColumnsType<TWorkflow> = [
    {
      title: t('workflow.code'),
      dataIndex: 'code',
      key: 'code',
      render: (text: string) => text
    },    
    {
      title: t('workflow.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) =>
        editingKey === record.id ? (
          <Input
            value={editingWorkflow?.name}
            onChange={e =>
              setEditingWorkflow(editingWorkflow && { ...editingWorkflow, name: e.target.value })
            }
          />
        ) : (
          text
        ),
    },
    {
      title: t('workflow.actions'),
      key: 'actions',
      fixed: 'right' as 'right',
      width: 160,
      render: (_: any, record: TWorkflow) => (
        <Space size="middle">
          {loadingRow === record.id ? (
            <Spin />
          ) : editingKey === record.id ? (
            <>
              <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
              <Button icon={<DeleteOutlined />} onClick={cancel} />
            </>
          ) : (
            <>
              <Button icon={<EditOutlined />} onClick={() => edit(record)} />
              <Button icon={<EyeOutlined />} onClick={() => showDetails(record)} />
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => confirmDelete(record.id)}
                loading={deleteLoading && deleteId === record.id}
              />
            </>
          )}
        </Space>
      ),
    },
  ];

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout 
      title={t('workflow.available')} 
      loading={loading}
      onBackNavigation={handleBackNavigation}
      showBackButton={true}
    >
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAdd}>
          {t('button.add')}
        </Button>
      </Space>
      <Table<TWorkflow>
        columns={columns}
        dataSource={workflows}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
      />

      <ConfirmationDialog
        visible={isDeleteDialogVisible}
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteDialogVisible(false)}
        loading={deleteLoading}
        title={t('workflow.confirmDeleteTitle')}
        description={t('workflow.confirmDeleteDescription')}
      />

      
    </AppLayout>
  );
};

export default WorkflowList;
